/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  useMediaQuery
} from '@mui/material';

import BlurryCard from 'src/components/blurryCard';
import BrandInputs from 'src/components/brandInputs';
import METRIC_CONFIGS from 'src/constants/metrics.constants';

import PropTypes from 'prop-types';

import BusinessCaseImage from 'src/assets/images/business_case_icon.png';
import SavingsImage from 'src/assets/images/savings_icon.png';
import VectorImage from 'src/assets/logos/Vector.svg';
import ArrowUpIcon from 'src/assets/logos/arrow-up.svg';

import {
  setBusinessCaseInputs,
  setBusinessCaseLoading,
  setBusinessCaseResult,
  setPrintHtmlPart as setPrintHtmlPartAction
} from 'src/store/actions';

import { sendBusinessCase } from 'src/store/services';
import buildBusinessCasePayload from 'src/utils/businessCasePayload.utils';
import useDebouncedEffect from 'src/hooks/useDebouncedEffect';

import useStyles from './useStyles';

const mapStateToProps = state => {
  const { questionsData, matchRulesResult, printHtml } = state.questionReducer;
  const geographyScope = matchRulesResult?.['Geography / Project Scope']?.[0];
  const { businessCase } = state;

  return {
    questionsData,
    geographyScope,
    inputs: businessCase.inputs,
    loading: businessCase.loading,
    current: businessCase.current,
    previous: businessCase.previous,
    printHtml
  };
};

const mapDispatchToProps = dispatch => ({
  setInputs: payload => dispatch(setBusinessCaseInputs(payload)),
  setLoading: loading => dispatch(setBusinessCaseLoading(loading)),
  setResult: payload => dispatch(setBusinessCaseResult(payload)),
  setPrintHtmlPart: (page, html) => dispatch(setPrintHtmlPartAction(page, html))
});

const fmtEUR = v =>
  typeof v === 'number'
    ? new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
      }).format(v)
    : '—';

function useAbortControllerRef() {
  const ref = useRef(null);
  useEffect(() => {
    return () => {
      if (ref.current) ref.current.abort();
    };
  }, []);
  return ref;
}

const extractPair = obj => {
  if (!obj || typeof obj !== 'object') {
    return { y1: null, y2: null, y1Key: 'Y1', y2Key: 'Y2' };
  }
  if ('Y1' in obj || 'Y2' in obj) {
    return { y1: obj.Y1 ?? null, y2: obj.Y2 ?? null, y1Key: 'Y1', y2Key: 'Y2' };
  }
  if ('Y1_GROSS' in obj || 'Y2_GROSS' in obj) {
    return {
      y1: obj.Y1_GROSS ?? null,
      y2: obj.Y2_GROSS ?? null,
      y1Key: 'Y1_GROSS',
      y2Key: 'Y2_GROSS'
    };
  }

  const numericKeys = Object.keys(obj).filter(k => typeof obj[k] === 'number');
  const [k1, k2] = numericKeys;
  return {
    y1: k1 ? obj[k1] : null,
    y2: k2 ? obj[k2] : null,
    y1Key: k1 || 'Y1',
    y2Key: k2 || 'Y2'
  };
};

function BusinessCasePage({
  questionsData,
  inputs,
  geographyScope,
  loading,
  current,
  setInputs,
  setLoading,
  setResult,
  businessRef
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width:640px)');
  const abortRef = useAbortControllerRef();

  const [selectedMetric, setSelectedMetric] = useState(
    'Estimated savings from redirecting returns'
  );

  const payload = useMemo(
    () => buildBusinessCasePayload(questionsData, inputs, geographyScope),
    [questionsData, inputs, geographyScope]
  );

  const validateInputs = useMemo(() => {
    // eslint-disable-next-line no-shadow
    return (inputs, availableMetrics) => {
      const seenKeys = new Set();
      const requiredFields = [];

      availableMetrics?.forEach(metric => {
        const config = METRIC_CONFIGS[metric];
        if (!config) return;

        config.inputs.forEach(field => {
          if (!seenKeys.has(field.key)) {
            seenKeys.add(field.key);
            requiredFields.push(field.key);
          }
        });
      });

      return requiredFields.every(key => {
        const value = inputs[key];
        return value !== 0;
      });
    };
  }, []);

  useDebouncedEffect(
    () => {
      // eslint-disable-next-line no-use-before-define
      if (!validateInputs(inputs, availableMetrics)) {
        setLoading(false);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      (async () => {
        try {
          setLoading(true);

          const resp = await sendBusinessCase(payload, {
            signal: controller.signal
          });
          setResult({ previous: current, current: resp });
        } catch (e) {
          if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
            setLoading(false);
          }
        }
      })();
    },
    [payload, validateInputs],
    600
  );

  const gs = current?.gross_summary || {};

  const allCountries = useMemo(() => {
    const countries = new Set();
    Object.keys(gs).forEach(metricKey => {
      if (gs[metricKey] && typeof gs[metricKey] === 'object') {
        Object.keys(gs[metricKey]).forEach(country => {
          if (
            gs[metricKey][country] &&
            typeof gs[metricKey][country] === 'object'
          ) {
            countries.add(country);
          }
        });
      }
    });
    return Array.from(countries);
  }, [gs]);

  const columnHeaders = allCountries;

  const rows = useMemo(() => {
    return Object.keys(gs)
      .filter(k => gs[k] && typeof gs[k] === 'object')
      .map(k => {
        const trimmedKey = (k || '').trim();
        const label =
          trimmedKey.toLowerCase() === 'gross_summary'
            ? t('businessCase.rows.gross_summary', 'Gross per year')
            : trimmedKey.replace(/_/g, ' ');

        return { key: k, label };
      });
  }, [gs, t]);

  const totalGross = gs?.gross_summary?.TOTAL_GROSS ?? null;

  const availableMetrics = useMemo(() => {
    const metrics = Object.keys(gs).filter(
      key => key !== 'gross_summary' && gs[key] && typeof gs[key] === 'object'
    );
    return metrics.length > 0 ? metrics : Object.keys(METRIC_CONFIGS);
  }, [gs]);

  useEffect(() => {
    if (
      !availableMetrics.includes(selectedMetric) &&
      availableMetrics.length > 0
    ) {
      setSelectedMetric(availableMetrics[0]);
    }
  }, [availableMetrics, selectedMetric]);

  const renderTableRows = () => {
    const regularRows = rows.filter(row => row.key !== 'gross_summary');
    const grossSummaryRow = rows.find(row => row.key === 'gross_summary');

    const tableRows = [];

    const filteredRows = regularRows.filter(row => {
      const metricData = gs[row.key] || {};

      return allCountries.some(country => {
        const countryData = metricData[country];
        const { y1, y2 } = extractPair(countryData);
        const value = y1 !== null ? y1 : y2;
        return value !== null && value > 1;
      });
    });

    filteredRows.forEach((row, idx) => {
      const metricData = gs[row.key] || {};

      tableRows.push(
        <TableRow
          key={row.key}
          className={
            idx === filteredRows.length - 1
              ? classes.bodyRowLast
              : classes.bodyRow
          }
          variant="Title-M-regular-sansSerif"
        >
          <TableCell className={classes.labelCell}>
            <Typography variant="Title-M-regular-sansSerif">
              {row.label}
            </Typography>
          </TableCell>

          {allCountries.map(country => {
            const countryData = metricData[country];
            const { y1, y2 } = extractPair(countryData);

            const value = y1 !== null ? y1 : y2;

            return (
              <TableCell
                key={`${row.key}-${country}`}
                className={classes.amountCell}
              >
                <Typography variant="Title-M-regular-sansSerif">
                  {fmtEUR(value)}
                </Typography>
              </TableCell>
            );
          })}
        </TableRow>
      );
    });
    if (grossSummaryRow) {
      tableRows.push(
        <TableRow key="gross_summary" className={classes.totalRow}>
          <TableCell className={classes.labelCell}>
            <Typography variant="Title-M-regular-sansSerif">
              {grossSummaryRow.label}
            </Typography>
          </TableCell>

          {allCountries.map(country => {
            let total = 0;
            filteredRows.forEach(row => {
              const metricData = gs[row.key] || {};
              const countryData = metricData[country];
              const { y1, y2 } = extractPair(countryData);
              const value = y1 !== null ? y1 : y2;
              if (typeof value === 'number') {
                total += value;
              }
            });

            return (
              <TableCell
                key={`gross-summary-${country}`}
                className={classes.amountCell}
              >
                <Typography variant="Title-M-regular-sansSerif">
                  {fmtEUR(total)}
                </Typography>
              </TableCell>
            );
          })}
        </TableRow>
      );
    }

    return tableRows;
  };

  const renderMobileRegionCards = () => {
    const rowsForRegion = rows.filter(row => row.key !== 'gross_summary');
    const regionsWithData = allCountries
      .map(region => {
        let regionTotal = 0;

        const goalItems = rowsForRegion
          .map(row => {
            const metricData = gs[row.key] || {};
            const countryData = metricData[region];
            const { y1, y2 } = extractPair(countryData);
            const value = y1 !== null ? y1 : y2;

            if (typeof value !== 'number' || value <= 1) return null;
            regionTotal += value;
            return { label: row.label, value, key: row.key };
          })
          .filter(Boolean);

        return goalItems.length > 0 ? { region, goalItems, regionTotal } : null;
      })
      .filter(Boolean);

    if (regionsWithData.length === 0) return null;

    return (
      <Box style={{ marginTop: '16px' }}>
        {regionsWithData.map((regionData, regionIndex) => (
          <Box key={`region-${regionData.region}`}>
            {regionIndex > 0 && (
              <Box
                height="1px"
                backgroundColor="rgba(var(--color-region-divider))"
                margin="16px 0"
              />
            )}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>{regionData.region}</Typography>
            </Box>
            <Box className={classes.mobileRegionBody}>
              {regionData.goalItems.map(item => (
                <Box
                  key={`${regionData.region}-${item.key}`}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  padding="8px 0px"
                >
                  {item.label && (
                    <Typography
                      variant="Title-M-regular-sansSerif"
                      className={classes.mobileRowLabel}
                    >
                      {item.label}
                    </Typography>
                  )}

                  <Typography className={classes.mobileRowValue}>
                    {fmtEUR(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box className={classes.mobileTotalRow}>
              <Typography
                variant="Title-M-regular-sansSerif"
                className={classes.mobileRowLabel}
              >
                {t('businessCase.rows.gross_summary', 'Gross per year')}
              </Typography>
              <Typography className={classes.mobileRowValue}>
                {fmtEUR(regionData.regionTotal)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      id="business-case-print-section"
      className={classes.pageWrapper}
      ref={businessRef}
    >
      <Box>
        <Box className={classes.headerWrapper}>
          <Box className={classes.headerColumn}>
            <Box className={classes.headerTitleRow}>
              <img src={BusinessCaseImage} alt="Business Case" />
              <Typography variant="Heading-4">
                {t('businessCase.title')}
              </Typography>
            </Box>
            <Typography variant="Body-XL-Regular">
              {t('businessCase.description')}
            </Typography>
          </Box>
        </Box>

        <BlurryCard>
          <Box className={classes.summaryHeader}>
            <img src={VectorImage} alt="Vector" />
            <Typography variant="Heading-4">
              {t('businessCase.summary')}
            </Typography>
            {loading && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Box>

          <Box className={classes.cardRow}>
            <Box className={classes.cardContent}>
              <img src={SavingsImage} alt="Savings" />
              <Box className={classes.revenueCard}>
                <Box className={classes.priceTitleCard}>
                  <Typography variant="Heading-S-Medium">
                    {t('businessCase.totalSavings')}
                  </Typography>
                </Box>
                <Box className={classes.priceCard}>
                  <Box className={classes.priceContent}>
                    <Box className={classes.priceValue}>
                      <Box className={classes.headerTitleRow}>
                        <img src={ArrowUpIcon} alt="Arrow Up" />
                        <Typography
                          variant="Heading-2"
                          style={{ color: 'rgb(var(--color-title-amount))' }}
                        >
                          {fmtEUR(totalGross)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {isMobile ? (
            <Box className={classes.mobileRegionsWrapper}>
              {renderMobileRegionCards()}
            </Box>
          ) : (
            <Table className={classes.summaryTable} variant>
              <TableHead>
                <TableRow className={classes.headRow}>
                  <TableCell className={classes.headCellLeft}>
                    <Typography variant="Title-M-regular-sansSerif">
                      {t('businessCase.metric', 'Region')}
                    </Typography>
                  </TableCell>

                  {columnHeaders.map(header => (
                    <TableCell key={header} className={classes.headCell}>
                      <Typography variant="Title-M-regular-sansSerif">
                        {header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody variant="Title-M-regular-sansSerif">
                {renderTableRows()}
              </TableBody>
            </Table>
          )}
        </BlurryCard>

        <BlurryCard style={{ marginTop: '24px' }}>
          <BrandInputs
            inputs={inputs}
            onChange={setInputs}
            selectedMetric={selectedMetric}
            onMetricChange={setSelectedMetric}
            availableMetrics={availableMetrics}
            t={t}
            classes={classes}
          />
        </BlurryCard>
      </Box>
    </Box>
  );
}

BusinessCasePage.propTypes = {
  questionsData: PropTypes.object.isRequired,
  inputs: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ).isRequired,
  geographyScope: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  loading: PropTypes.bool.isRequired,
  current: PropTypes.object,
  setInputs: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setResult: PropTypes.func.isRequired,
  businessRef: PropTypes.shape({
    current: PropTypes.any
  })
};

export default connect(mapStateToProps, mapDispatchToProps)(BusinessCasePage);
